import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import Toggle from "../../components/Toggle";

const EMPTY = { title: "", slug: "", content: "", published: true, in_nav: false, in_footer: true, order: 0 };

export default function AdminPages({ onChanged }) {
  const [pages, setPages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/pages?all=true").then((r) => setPages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refreshAll = () => { load(); onChanged?.(); };

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, slug: form.slug, content: form.content,
      published: form.published, in_nav: form.in_nav, in_footer: form.in_footer,
      order: parseInt(form.order) || 0,
    };
    try {
      if (editing) await api.put(`/pages/${editing}`, payload);
      else await api.post("/pages", payload);
      toast.success(editing ? "Page updated" : "Page created");
      setShowForm(false);
      refreshAll();
    } catch {
      toast.error("Could not save page");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    await api.delete(`/pages/${id}`);
    toast.success("Page deleted");
    refreshAll();
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="mt-6">
      <button data-testid="admin-add-page" onClick={openNew} className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors mb-4">
        <Plus className="w-4 h-4" /> Add Page
      </button>

      <div className="space-y-3">
        {pages.length === 0 && <p className="text-[#5C665D]">No pages yet.</p>}
        {pages.map((p) => (
          <div key={p.id} data-testid={`admin-page-${p.slug}`} className={`bg-white border border-[#E8E4D9] rounded-xl p-4 flex items-center justify-between gap-3 ${p.published ? "" : "opacity-60"}`}>
            <div className="min-w-0">
              <p className="font-display font-medium text-[#1A231C] truncate">{p.title}</p>
              <p className="text-xs text-[#5C665D]">
                /{p.slug} · {p.published ? "Published" : "Draft"}
                {p.in_nav ? " · Nav" : ""}{p.in_footer ? " · Footer" : ""}
              </p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Link to={`/page/${p.slug}`} target="_blank" className="text-[#5C665D] p-1.5 rounded hover:bg-[#F4EFE6]"><ExternalLink className="w-4 h-4" /></Link>
              <button data-testid={`page-edit-${p.slug}`} onClick={() => openEdit(p)} className="text-[var(--brand-primary)] p-1.5 rounded hover:bg-[#F4EFE6]"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`page-delete-${p.slug}`} onClick={() => del(p.id)} className="text-[var(--brand-accent)] p-1.5 rounded hover:bg-[#F4EFE6]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg text-[#1A231C]">{editing ? "Edit Page" : "New Page"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[#5C665D]" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="page-form-title" required value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Page title (e.g. About Us)" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <input data-testid="page-form-slug" value={form.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="URL slug (optional, e.g. about)" className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <textarea data-testid="page-form-content" value={form.content} onChange={(e) => setField("content", e.target.value)} rows={8} placeholder="Page content..." className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)]" />
              <div className="space-y-2">
                <div className="flex items-center justify-between border border-[#E8E4D9] rounded-lg px-4 py-2.5">
                  <span className="text-sm text-[#1A231C]">Published (visible on site)</span>
                  <Toggle checked={form.published} onChange={(v) => setField("published", v)} testid="page-form-published" />
                </div>
                <div className="flex items-center justify-between border border-[#E8E4D9] rounded-lg px-4 py-2.5">
                  <span className="text-sm text-[#1A231C]">Show in top navigation</span>
                  <Toggle checked={form.in_nav} onChange={(v) => setField("in_nav", v)} testid="page-form-nav" />
                </div>
                <div className="flex items-center justify-between border border-[#E8E4D9] rounded-lg px-4 py-2.5">
                  <span className="text-sm text-[#1A231C]">Show in footer</span>
                  <Toggle checked={form.in_footer} onChange={(v) => setField("in_footer", v)} testid="page-form-footer" />
                </div>
              </div>
            </div>
            <button data-testid="page-form-save" type="submit" className="w-full mt-5 bg-[var(--brand-primary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors">
              {editing ? "Update" : "Create"} Page
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
